/**
 * SyncStalenessBanner.tsx — fleet-wide "nothing is syncing" alarm.
 *
 * WHY (2026-08-24): a Decodo proxy traffic cap froze every Maloum request on 2026-08-21 and the
 * CRM went three days without ingesting a single message — while the UI looked completely normal.
 * The only clue was "3d ago" timestamps in the chat list, which reads as quiet fans rather than a
 * dead pipeline. This banner makes that failure impossible to miss: it sits under the admin nav on
 * every page and states, in plain words, how long it has been since ANY account reached Maloum.
 *
 * Deliberately quiet when healthy — it renders nothing at all unless the fleet is genuinely
 * stalled, so it never becomes wallpaper that operators learn to scroll past.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { healthApi, type SyncFreshnessDto } from '../lib/api-client.js';

/** Poll cadence — the watchdog itself only recomputes every 60s, so faster polling buys nothing. */
const REFETCH_MS = 60_000;

/** "3d 1h" / "12m" — coarse on purpose; the exact second is noise at this scale. */
function formatAge(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}

export function SyncStalenessBanner(): React.ReactElement | null {
  const { data } = useQuery<SyncFreshnessDto>({
    queryKey: ['health', 'sync-freshness'],
    queryFn: () => healthApi.getSyncFreshness(),
    refetchInterval: REFETCH_MS,
    // Keep checking while the tab sits in the background: an outage that starts overnight should
    // already be on screen when someone looks, not only after they click something.
    refetchIntervalInBackground: true,
    // A failing request must never itself render an alarm — that would cry wolf on a blip.
    retry: false,
  });

  if (!data || !data.monitored || !data.fleetStalled) return null;

  const age = data.newestSyncAgeMs !== null ? formatAge(data.newestSyncAgeMs) : null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="border-b border-danger/40 bg-danger/10 px-4 py-2 text-xs text-text flex flex-wrap items-center gap-x-2 gap-y-1"
    >
      <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-danger animate-pulse" />
      <span className="font-semibold text-danger">Sync stalled</span>
      <span className="text-text-muted">
        {age
          ? `No account has reached Maloum in ${age}.`
          : 'No account has reached Maloum since this process started.'}{' '}
        {data.expectedAccounts} account{data.expectedAccounts === 1 ? '' : 's'} affected — incoming
        messages and transactions are not being ingested.
      </span>
      <span className="text-text-subtle">
        Most likely the egress proxy (traffic limit or dead pool). Check the Decodo traffic meter first.
      </span>
    </div>
  );
}
